import React, { useEffect, useState } from "react";
import { LOGO_URL } from "../data/site";

// Client-side background remover: loads the source PNG, and turns near-white
// pixels transparent so the logo can sit cleanly on a dark background.
export default function Logo({ className = "", alt = "Eco Sweeps LLC" }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const px = data.data;
        // Two-pass pixel processing:
        //  1. Near-white pixels -> fully transparent
        //  2. Dark low-saturation pixels (navy cloud + wordmark) -> lifted to
        //     a light slate so they are legible on a dark background.
        //  3. Cyan / colored accents preserved.
        for (let i = 0; i < px.length; i += 4) {
          const r = px[i], g = px[i + 1], b = px[i + 2];
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const avg = (r + g + b) / 3;
          const chroma = max - min;
          if (avg > 235 && chroma < 15) {
            // white background -> transparent
            px[i + 3] = 0;
          } else if (avg > 210 && chroma < 20) {
            // soft antialiased edge -> fade alpha
            const t = (avg - 210) / 25;
            px[i + 3] = Math.max(0, Math.min(255, px[i + 3] * (1 - t)));
          } else if (avg < 140 && r < 120 && g < 120) {
            // dark navy brand ink (cloud outline + wordmark) -> pure white
            // Preserve cyan accents which have high g+b values.
            px[i] = 255;
            px[i + 1] = 255;
            px[i + 2] = 255;
          }
        }
        ctx.putImageData(data, 0, 0);
        setSrc(canvas.toDataURL("image/png"));
      } catch (e) {
        // If CORS or canvas fails, fall back to the raw image.
        setSrc(LOGO_URL);
      }
    };
    img.onerror = () => { if (!cancelled) setSrc(LOGO_URL); };
    img.src = LOGO_URL;
    return () => { cancelled = true; };
  }, []);

  if (!src) {
    // Skeleton placeholder to prevent layout shift
    return <div className={className} aria-label={alt} />;
  }
  return <img src={src} alt={alt} className={className} />;
}
